import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type ReviewSource = "homedeal" | "google";

export type PublicReview = {
  title: string;
  author: string;
  isoDate: string;
  /** Schaal 1-10 (Google 1-5 sterren wordt ×2 omgerekend). */
  rating: number;
  text: string;
  source: ReviewSource;
};

export type PublicReviewsPayload = {
  reviews: PublicReview[];
  /** Gezamenlijk gewogen gemiddelde op schaal 1-10. */
  avgRating: number | null;
  /** Totaal aantal reviews over beide bronnen. */
  reviewCount: number | null;
  syncedAt: string | null;
  /** Actuele cijfers per bron (null zolang er nog niet gesynchroniseerd is). */
  homedeal: { avgRating: number; reviewCount: number } | null;
  google: { avgRating: number; reviewCount: number } | null;
};

/** Publieke, read-only lijst met de meest recente reviews (Homedeal + Google), nieuwste eerst. */
export const getPublicReviews = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicReviewsPayload> => {
    const url = process.env["SUPABASE_URL"];
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) {
      return { reviews: [], avgRating: null, reviewCount: null, syncedAt: null, homedeal: null, google: null };
    }

    const supabasePublic = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
            h.delete("Authorization");
          }
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });

    const [
      { data: homedealRows },
      { data: homedealSummary },
      { data: googleRows },
      { data: googleSummary },
    ] = await Promise.all([
      supabasePublic
        .from("homedeal_reviews")
        .select("title, author, rating, published_at, body")
        .order("published_at", { ascending: false })
        .limit(24),
      supabasePublic
        .from("homedeal_review_summary")
        .select("avg_rating, review_count, synced_at")
        .maybeSingle(),
      supabasePublic
        .from("google_reviews")
        .select("author, rating, published_at, body")
        .order("published_at", { ascending: false })
        .limit(24),
      supabasePublic
        .from("google_review_summary")
        .select("avg_rating, review_count, synced_at")
        .maybeSingle(),
    ]);

    const homedealReviews: PublicReview[] = (homedealRows ?? [])
      .filter((r) => (r.body ?? "").trim().length > 0)
      .map((r) => ({
        title: r.title ?? "",
        author: r.author ?? "Klant",
        isoDate: new Date(r.published_at).toISOString().slice(0, 10),
        rating: Number(r.rating ?? 10),
        text: r.body ?? "",
        source: "homedeal" as const,
      }));

    const googleReviews: PublicReview[] = (googleRows ?? [])
      .filter((r) => (r.body ?? "").trim().length > 0)
      .map((r) => ({
        title: "",
        author: r.author ?? "Klant",
        isoDate: new Date(r.published_at).toISOString().slice(0, 10),
        // Google 1-5 sterren → schaal 1-10
        rating: Number(r.rating ?? 5) * 2,
        text: r.body ?? "",
        source: "google" as const,
      }));

    const reviews = [...homedealReviews, ...googleReviews].sort(
      (a, b) => new Date(b.isoDate).getTime() - new Date(a.isoDate).getTime(),
    );

    // Gewogen gemiddelde op schaal 1-10 over beide bronnen.
    const hdCount = homedealSummary?.review_count ?? 0;
    const gCount = googleSummary?.review_count ?? 0;
    const hdAvg = homedealSummary?.avg_rating != null ? Number(homedealSummary.avg_rating) : null;
    const gAvg = googleSummary?.avg_rating != null ? Number(googleSummary.avg_rating) * 2 : null;

    let avgRating: number | null = null;
    let reviewCount: number | null = null;
    const totalCount = hdCount + gCount;
    if (totalCount > 0) {
      const weighted =
        (hdAvg != null ? hdAvg * hdCount : 0) + (gAvg != null ? gAvg * gCount : 0);
      const counted = (hdAvg != null ? hdCount : 0) + (gAvg != null ? gCount : 0);
      avgRating = counted > 0 ? Math.round((weighted / counted) * 10) / 10 : null;
      reviewCount = totalCount;
    }

    const syncedAt = [homedealSummary?.synced_at, googleSummary?.synced_at]
      .filter(Boolean)
      .sort()
      .pop() ?? null;

    return {
      reviews,
      avgRating,
      reviewCount,
      syncedAt,
      homedeal: hdAvg != null && hdCount > 0 ? { avgRating: hdAvg, reviewCount: hdCount } : null,
      google:
        googleSummary?.avg_rating != null && gCount > 0
          ? { avgRating: Number(googleSummary.avg_rating), reviewCount: gCount }
          : null,
    };
  },
);
